import { useState, useEffect } from "react";
import { T, TF } from "react-translator-component";
import {
  Badge, Box, IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import apiServices from "../services/apiServices";
import AdminPageFrame from "../components/AdminPageFrame";
import LoadingModal from "../../components/LoadingModal";
import ContainerPaper from "../components/ContainerPaper";
import ReadMore from "../components/ReadMore";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { mngStyles } from "../constants/styles";
import { isAdmin } from "../util/util";
import { useSelector } from "react-redux";
import GroupForm from "../components/groupList/GroupForm";

export default function GroupList() {
  const { admin } = useSelector((state: any) => state.adminAuth);
  const dispatch = useAppDispatch();
  const [showFormDialog, setShowFormDialog] = useState<boolean>(false);
  const [showDeleteConfirmModallete, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [editGroup, setEditGroup] = useState<any>();
  const [deleteGroup, setDeleteGroup] = useState<any>();
  const [pagination, setPagination] = useState<any>({});

  const fetchGroup = async (page: number) => {
    if (!page) return

    try {
      setLoader(true);
      const res = await apiServices.getGroups(page);
      if (page === 1) {
        setGroups(res.groups);
      } else {
        setGroups([...groups, ...res.groups]);
      }
      setPagination(res.pagination);
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchGroup(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOpen = (group?: any) => {
    setEditGroup(group);
    setShowFormDialog(true);
  };

  const handleOpenDeleteConfirmModal = (group: any) => {
    setShowDeleteConfirmModal(true);
    setDeleteGroup(group);
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!isAdmin(admin) || !deleteGroup?.id || deleteGroup?.posts_count > 0) {
      return false
    }

    try {
      setLoader(true);
      await apiServices.deleteGroup(deleteGroup.id);
      handleCloseConfirmDialog()
      fetchGroup(1);
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setLoader(false);
    }
  };

  const handleCloseFormDialog = () => {
    setEditGroup(undefined)
    setShowFormDialog(false);
  };

  const handleCloseConfirmDialog = () => {
    setDeleteGroup(null)
    setShowDeleteConfirmModal(false)
  };

  return (
    <AdminPageFrame>
      <LoadingModal visible={loader} />

      <ContainerPaper>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="caption table">
            <TableHead>
              <TableRow>
                <TableCell>{T("mng_data_definitions_groups")}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" style={{ width: 200 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <IconButton color="inherit" onClick={() => handleClickOpen()}>
                      <Badge color="secondary">
                        <CreateIcon />
                        <Typography>
                          {T("mng_create_group")}
                        </Typography>
                      </Badge>
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {groups.map((group: any) => (
                <TableRow key={group.id}>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={mngStyles.titletable}>
                        {group.name}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="subtitle1" style={mngStyles.titletable}>
                      {TF(T("mng_group_post_count"), group.posts_count.toLocaleString())}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    {isAdmin(admin) && (
                      <IconButton onClick={() => handleClickOpen(group)}>
                        <EditIcon color="primary" />
                      </IconButton>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {group.posts_count === 0 && (
                      <IconButton onClick={() => handleOpenDeleteConfirmModal(group)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <ReadMore open={pagination?.next_page > 0} onClick={() => fetchGroup(pagination?.next_page)} />
        </TableContainer>
      </ContainerPaper>

      <GroupForm
        show={showFormDialog}
        group={editGroup ?? undefined}
        onReload={() => fetchGroup(1)}
        onClose={() => handleCloseFormDialog()}
      />

      <DeleteConfirmationDialog
        message={T('mng_group_delete_confirmation_message')}
        open={showDeleteConfirmModallete}
        validate={async () => deleteGroup !== undefined}
        onSubmit={handleDelete}
        onClose={() => handleCloseConfirmDialog()}
      />
    </AdminPageFrame>
  );
}
